import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Check } from "lucide-react";
import { useEffect, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { useAuth } from "@/hooks/use-auth";
import { getFoundingPlaces } from "@/lib/pricing.functions";

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "Pricing — free for the first 49 students · Tiger's Eye" },
      {
        name: "description",
        content:
          "Tiger's Eye is free for the first 49 students, then £4.95 per term. Impartial academic critique of your essay or dissertation draft — never ghostwriting.",
      },
      { property: "og:url", content: "https://tigerseye.app/pricing" },
      {
        property: "og:title",
        content: "Pricing — free for the first 49 students · Tiger's Eye",
      },
      {
        property: "og:description",
        content:
          "Tiger's Eye is free for the first 49 students, then £4.95 per term. Impartial academic critique of your essay or dissertation draft — never ghostwriting.",
      },
    ],
    links: [{ rel: "canonical", href: "https://tigerseye.app/pricing" }],
  }),
  component: PricingPage,
});

const BG = "var(--paper)";
const SURFACE = "var(--card)";
const INK = "var(--primary)";
const INK_SOFT = "var(--ink-secondary)";
const INK_FAINT = "var(--ink-tertiary)";
const BRASS = "var(--editorial-accent, var(--accent))";
const RULE = "var(--rule)";

const SERIF =
  '"Iowan Old Style", "Source Serif Pro", "Source Serif 4", "Charter", Georgia, ui-serif, serif';

const INCLUDED = [
  "Full critique of every draft you upload",
  "Checked against your brief, level and referencing style",
  "Clear, section-by-section comments you can act on",
  "Critique only — we never write or rewrite your work",
  "Your drafts stay yours; nothing is shared or sold",
];

export default function PricingPage() {
  const { user } = useAuth();
  const ctaTo = user ? "/reviews/new" : "/signup";
  const [places, setPlaces] = useState<{ claimed: number; total: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    getFoundingPlaces()
      .then((result) => {
        if (!cancelled) setPlaces(result);
      })
      .catch(() => {
        if (!cancelled) setPlaces(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const pct = places ? Math.round((places.claimed / places.total) * 100) : 0;

  return (
    <main className="min-h-screen w-full" style={{ background: BG, color: INK }}>
      <style>{`
        .hv4-serif { font-family: ${SERIF}; font-feature-settings: "liga","kern"; }
        .hv4-eyebrow {
          font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
          font-weight: 600; color: ${BRASS};
        }
        .hv4-cta { transition: transform .18s ease, box-shadow .18s ease, background .18s ease; }
        .hv4-cta-primary { background: ${INK}; color: ${BG}; }
        .hv4-cta-primary:hover { transform: translateY(-1px); box-shadow: 0 10px 22px -14px ${INK}; background: color-mix(in oklch, ${INK} 85%, black); }
      `}</style>

      <header
        className="sticky top-0 z-20"
        style={{ background: BG, borderBottom: `1px solid ${RULE}` }}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="hv4-serif text-lg font-semibold" style={{ color: INK }}>
            Tiger&apos;s Eye
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard" className="text-sm" style={{ color: INK_SOFT }}>
                Dashboard
              </Link>
            ) : (
              <Link to="/login" className="text-sm" style={{ color: INK_SOFT }}>
                Sign in
              </Link>
            )}
            <Link
              to={ctaTo}
              className="hv4-cta hv4-cta-primary inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium"
            >
              Review my draft
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-3xl px-6 pb-24 pt-16 sm:pt-24">
        <Reveal>
          <p className="hv4-eyebrow">Pricing</p>
          <h1
            className="hv4-serif mt-4 text-balance text-4xl leading-tight sm:text-5xl"
          >
            Practically free. Honestly priced.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed" style={{ color: INK_SOFT }}>
            Good feedback shouldn&apos;t cost more than your textbooks. Tiger&apos;s Eye
            keeps prices low so every student can afford a second pair of eyes.
          </p>
        </Reveal>

        <Reveal>
          <div
            className="mt-10 rounded-2xl p-6"
            style={{ background: SURFACE, border: `1px solid ${RULE}` }}
            role="status"
            aria-live="polite"
          >
            <div className="flex items-baseline justify-between gap-4">
              <p className="text-sm font-medium">
                Founding places are going fast
              </p>
              <p className="text-sm tabular-nums" style={{ color: INK_SOFT }}>
                {places ? (
                  <>
                    <strong style={{ color: INK }}>{places.claimed}</strong> of{" "}
                    {places.total} claimed
                  </>
                ) : (
                  "Checking…"
                )}
              </p>
            </div>
            <div
              className="mt-3 h-2.5 w-full overflow-hidden rounded-full"
              style={{ background: `color-mix(in oklch, ${INK} 10%, ${BG})` }}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={places?.total ?? 49}
              aria-valuenow={places?.claimed ?? 0}
              aria-label="Founding places claimed"
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background: BRASS,
                  transition: "width 1s ease",
                }}
              />
            </div>
            <p className="mt-3 text-xs" style={{ color: INK_FAINT }}>
              Once all {places?.total ?? 49} places are taken, new students pay
              £4.95 per term.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          <Reveal>
            <div
              className="flex h-full flex-col rounded-2xl p-7"
              style={{ background: SURFACE, border: `1px solid ${RULE}` }}
            >
              <p className="hv4-eyebrow">Founding students</p>
              <p className="hv4-serif mt-4 text-4xl">Free</p>
              <p className="mt-2 text-sm" style={{ color: INK_SOFT }}>
                For the first 49 students who sign up.
              </p>
              <ul className="mt-6 flex flex-col gap-2.5 text-sm" style={{ color: INK_SOFT }}>
                {INCLUDED.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: BRASS }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={ctaTo}
                className="hv4-cta hv4-cta-primary mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium"
              >
                Claim your free place
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>

          <Reveal>
            <div
              className="flex h-full flex-col rounded-2xl p-7"
              style={{ background: SURFACE, border: `1px solid ${RULE}` }}
            >
              <p className="hv4-eyebrow">Everyone after that</p>
              <p className="hv4-serif mt-4 text-4xl">
                £4.95 <span className="text-lg" style={{ color: INK_FAINT }}>/ term</span>
              </p>
              <p className="mt-2 text-sm" style={{ color: INK_SOFT }}>
                Less than a coffee a month, for a whole term of reviews.
              </p>
              <ul className="mt-6 flex flex-col gap-2.5 text-sm" style={{ color: INK_SOFT }}>
                {INCLUDED.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: BRASS }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-xs leading-relaxed" style={{ color: INK_FAINT }}>
                Once the first 49 places are taken, new students pay £4.95 per term.
                No hidden fees, no word-count charges, cancel any time.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal>
          <p className="mt-12 text-center text-sm" style={{ color: INK_FAINT }}>
            Every review is a critique of your own writing — Tiger&apos;s Eye never writes
            or rewrites your work, so your grade stays yours.{" "}
            <Link to="/versus" className="underline" style={{ color: INK_SOFT }}>
              How we differ from AI writing tools
            </Link>
          </p>
          <p className="mt-4 text-center text-sm" style={{ color: INK_SOFT }}>
            Questions?{" "}
            <Link to="/faq" className="underline" style={{ color: INK }}>
              Read the student FAQ
            </Link>
            {" "}· New here?{" "}
            <Link to="/free-review" className="underline" style={{ color: INK }}>
              See the free-review offer
            </Link>
            {" "}or{" "}
            <Link to="/blog" className="underline" style={{ color: INK }}>
              read the blog
            </Link>
            {" "}· Got a code from a YouTuber or your university?{" "}
            <Link to="/redeem" className="underline" style={{ color: INK }}>
              Redeem it here
            </Link>
            .
          </p>
        </Reveal>
      </section>

      <footer
        className="mx-auto flex max-w-6xl items-center justify-between px-6 pb-10 text-xs"
        style={{ color: INK_FAINT }}
      >
        <span>© {new Date().getFullYear()} Tiger&apos;s Eye · by PRAXIA</span>
        <span className="flex items-center gap-4">
          <Link to="/free-review" style={{ color: INK_SOFT }}>
            Free review
          </Link>
          <Link to="/blog" style={{ color: INK_SOFT }}>
            Blog
          </Link>
          <Link to="/faq" style={{ color: INK_SOFT }}>
            FAQ
          </Link>
          <Link to="/versus" style={{ color: INK_SOFT }}>
            Vs AI writing tools
          </Link>
          <Link to="/terms" style={{ color: INK_SOFT }}>
            Terms &amp; ethos
          </Link>
        </span>
      </footer>
    </main>
  );
}
